"use client";

import Image from "next/image";
import Link from "next/link";
import { FacebookIcon, InstagramIcon, TiktokIcon, BackgroundFooter } from "@/components/shared/svg/Icons";
import { useSettings } from "@/features/settings/hook/useSettings";
import { MessageCircle, MapPin } from "lucide-react";

const Footer = () => {
    const { data: settings } = useSettings();

    const socialLinks = [
        {
            icon: <FacebookIcon />,
            href: settings?.social?.facebook ? `https://facebook.com/${settings.social.facebook}` : null,
            label: "Facebook"
        },
        {
            icon: <InstagramIcon />,
            href: settings?.social?.instagram ? `https://instagram.com/${settings.social.instagram.replace("@", "")}` : null,
            label: "Instagram"
        },
        {
            icon: <TiktokIcon />,
            href: settings?.social?.tiktok ? `https://tiktok.com/@${settings.social.tiktok.replace("@", "")}` : null,
            label: "TikTok"
        },
        {
            icon: <MessageCircle className="w-6 h-6 text-white" />,
            href: settings?.social?.whatsapp ? `https://wa.me/${settings.social.whatsapp.replace(/\D/g, "")}` : null,
            label: "WhatsApp"
        }
    ].filter(link => link.href);


    return (
        <footer className="relative w-full bg-primary-50 text-primary-700 overflow-hidden border-t-2 border-primary-200">
            {/* Background pattern */}
            <div className="absolute inset-0 pointer-events-none">
                <BackgroundFooter className="object-cover " />
            </div>

            {/* Content */}
            <div className="relative z-10  pt-16 pb-12 container px-2 mx-auto">
                <div className="grid grid-cols-2 gap-12 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 items-start ">

                    {/* Logo + Social */}
                    <div className="flex flex-col items-center sm:items-start gap-8 col-span-2 md:col-span-1">
                        <Link href="/">
                            <Image
                                src={settings?.logoUrl || "/assets/logo/logo.png"}
                                alt={`Logo ${settings?.siteName || "Ezzahraoui"}`}
                                width={200}
                                height={200}
                                className="w-45 lg:w-50 h-auto object-contain"
                                loading="lazy"
                            />
                        </Link>

                        <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
                            {socialLinks.map((social) => (
                                <Link
                                    key={social.label}
                                    href={social.href!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 transition-all hover:bg-primary-600 hover:scale-110 shadow-sm"
                                    aria-label={social.label}
                                >
                                    {social.icon}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="space-y-4 text-center sm:text-left">
                        <Link href="/contact">
                            <h3 className="text-xl font-medium text-primary-600 mb-2 ">Contact</h3>
                        </Link>
                        <ul className="space-y-2 text-sm">
                            <li><Link href="/contact" className="hover:text-primary-900 transition-colors">Service Client</Link></li>
                            <li><Link href="/help" className="hover:text-primary-900 transition-colors">Centre d&apos;aide</Link></li>
                            <li><Link href="/orders/track" className="hover:text-primary-900 transition-colors">Suivre votre commande</Link></li>
                        </ul>
                    </div>

                    {/* Service Client */}
                    <div className="space-y-4 text-center sm:text-left">
                        <h3 className="text-xl font-medium text-primary-600">
                            Service Client
                        </h3>
                        <ul className="space-y-2 text-sm">
                            <li><Link href="/faq" className="hover:text-primary-900 transition-colors">FAQ</Link></li>
                            <li><Link href="/shipping" className="hover:text-primary-900 transition-colors">Livraison & Retours</Link></li>
                        </ul>
                    </div>

                    {/* Address */}
                    <div className="space-y-4 text-center sm:text-left col-span-2 md:col-span-1">
                        <Link href="/boutiques" className="group">
                            <h3 className="text-xl font-medium text-primary-600 transition-colors group-hover:text-primary-900">Nos Boutiques</h3>
                        </Link>
                        <ul className="space-y-4 text-sm leading-relaxed">
                            {settings?.locations && settings.locations.length > 0 ? (
                                settings.locations.map((loc) => {
                                    const googleMapsUrl = loc.latitude && loc.longitude 
                                        ? `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`
                                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${loc.address}, ${loc.city || ""}, Algeria`)}`;

                                    return (
                                        <li key={loc.id} className="flex flex-col gap-2 border-b border-primary-100/30 pb-4 last:border-0 last:pb-0 group/loc">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="font-semibold text-primary-800 group-hover/loc:text-primary-950 transition-colors uppercase tracking-tight text-[13px]">{loc.name || loc.city}</span>
                                                <p className="text-primary-600/90 text-[13px] leading-snug">
                                                    {loc.address}
                                                    {loc.city && `, ${loc.city}`}
                                                </p>
                                            </div>
                                            
                                            <div className="flex flex-col gap-1.5 text-[12px]">
                                                {/* Google Maps Link */}
                                                <Link 
                                                    href={googleMapsUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 text-primary-500 hover:text-primary-900 font-medium transition-colors w-fit"
                                                >
                                                    <MapPin className="w-3 h-3" />
                                                    <span>Localiser sur Maps</span>
                                                </Link>

                                                <div className="flex flex-wrap gap-x-4 gap-y-1 opacity-80">
                                                    {loc.email && (
                                                        <a href={`mailto:${loc.email}`} className="hover:text-primary-950 underline underline-offset-2 transition-colors">
                                                            {loc.email}
                                                        </a>
                                                    )}
                                                    {loc.phone && (
                                                        <a href={`tel:${loc.phone}`} className="hover:text-primary-950 font-medium transition-colors">
                                                            {loc.phone}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })
                            ) : (
                                <>
                                    <li className="flex flex-col gap-1">
                                        <span className="font-semibold text-primary-800">Boutique Mostaganem</span>
                                        <p className="text-primary-600/80">
                                            27000, Boulevard de la Salamandre, Mostaganem.
                                        </p>
                                        <div className="flex flex-col gap-0.5 mt-1 text-[12px]">
                                            <span className="text-primary-600/80">Email: Commercial@Zahraoui.com</span>
                                            <span className="text-primary-600/80">Phone: +213 27 31 16 23</span>
                                        </div>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>

                {/* Copyright */}

                <div className="mt-16 text-center text-md text-primary-600">
                    <Link href="https://www.elaris-solution.com/" target="_blank">
                        © 2026 Elaris Solution | Tous droits réservés.
                    </Link>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
